import { useCallback, useEffect, useState } from 'react';
import { Icon, Panel, Stat, useErrorDialog } from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { PasswordPanel } from './PasswordPanel.tsx';
import { RecoveryCodesPanel } from './RecoveryCodesPanel.tsx';
import { AuthenticatorPanel } from './AuthenticatorPanel.tsx';
import './account.css';

/**
 * YOUR OWN ACCOUNT — the screen the console did not have.
 *
 * ── WHY THIS SCREEN EXISTS ────────────────────────────────────────────────
 *
 * Not "it was awkward". There was no route and no screen. A console
 * administrator could not change their own password, could not see whether they
 * had any recovery codes left — they are shown exactly once, so somebody who had
 * spent a few had no way to know — and could not move their authenticator to a new
 * phone without asking a Super Admin to wipe the second factor off the account
 * entirely. Players had all four of these. Partners had the password half. The
 * realm that moves money and holds `funds.approve` had none of them.
 *
 * ── IT IS NOT A SIDEBAR MODULE, AND THAT IS NOT AN OVERSIGHT ──────────────
 *
 * `ACCOUNT_MENU_ROUTES` in `account-menu.ts` carries the reasoning: this has no
 * permission key, every role has it, and the product IA names twenty modules — a
 * count three assertions and the documented claim depend on. Adding it to
 * `NAV_GROUPS` would make the console twenty-one modules in order to satisfy a
 * test about orphaned routes. The escape is named and shared instead, so the
 * shell and the tests read the same list.
 *
 * ── THE ORDER OF THE PANELS IS THE ORDER OF THE QUESTIONS ─────────────────
 *
 * Password first, because it is the thing people come here for. Then the
 * authenticator, then the codes that back it up, then where you are signed in.
 * Not alphabetical, and not grouped by how the server happens to be organised.
 *
 * ── AND EVERY WRITE RELOADS THE WHOLE THING ───────────────────────────────
 *
 * Deliberately, rather than patching the counts locally. Changing a password ends
 * other sessions and un-trusts devices; replacing an authenticator does that AND
 * replaces the recovery codes. A screen that tried to predict all of that would
 * eventually disagree with the server about the state of somebody's credentials,
 * which is the one place a stale number is worse than a spinner.
 */
interface AccountView {
  email: string;
  displayName: string;
  role: string;
  passwordUpdatedAt: string | null;
  passwordMinimum: number;
  twoFactorConfirmedAt: string | null;
  recoveryCodesRemaining: number;
  pendingTwoFactorExpiresAt: string | null;
  otherSessions: number;
  trustedDevices: number;
}

export function Account() {
  const { showError } = useErrorDialog();
  const [view, setView] = useState<AccountView | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  /* What just happened, in one line under the heading. Not a toast: the console
     has no toast provider, and a line that persists is better here anyway —
     "3 other sessions were signed out" is something somebody re-reads. */
  const [lastChange, setLastChange] = useState<string | null>(null);

  const failed = useCallback(
    (title: string, error: unknown) => {
      showError({
        title,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was changed. Check your connection and try again.',
      });
    },
    [showError],
  );

  const load = useCallback(async () => {
    try {
      setView(await api.get<AccountView>('/api/auth/account'));
      setLoadFailed(false);
    } catch (error) {
      setLoadFailed(true);
      failed('Could not load your account', error);
    }
  }, [failed]);

  useEffect(() => {
    void load();
  }, [load]);

  const sentence = (sessions: number, devices: number): string => {
    const parts: string[] = [];
    if (sessions > 0) {
      parts.push(sessions === 1 ? 'one other session was signed out' : `${sessions} other sessions were signed out`);
    }
    if (devices > 0) {
      parts.push(
        devices === 1
          ? 'one saved device will ask for a code again'
          : `${devices} saved devices will ask for a code again`,
      );
    }
    if (parts.length === 0) return 'Nothing else was signed in.';
    const joined = parts.join(', and ');
    return joined.charAt(0).toUpperCase() + joined.slice(1) + '.';
  };

  if (loadFailed && view === null) {
    return (
      <div className="stack">
        <ScreenHeader
          title="Your account"
          purpose="Your own password, authenticator and recovery codes. Nothing here touches anybody else's account."
        />
        <div className="notice notice-danger">
          <Icon name="warning" />
          <div>
            Your account details could not be loaded, so nothing on this screen can be changed
            safely. Reload the page.
          </div>
        </div>
      </div>
    );
  }

  if (view === null) {
    return (
      <div className="stack">
        <ScreenHeader
          title="Your account"
          purpose="Your own password, authenticator and recovery codes. Nothing here touches anybody else's account."
        />
        <div className="skeleton" style={{ height: '260px' }}>
          &nbsp;
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <ScreenHeader
        title="Your account"
        purpose="Your own password, authenticator and recovery codes. Nothing here touches anybody else's account."
        meta={
          <>
            {view.displayName} · {view.email} · {view.role}
          </>
        }
      />

      {lastChange !== null && (
        <div className="notice">
          <Icon name="check" />
          <div>{lastChange}</div>
        </div>
      )}

      <PasswordPanel
        minimum={view.passwordMinimum}
        updatedAt={view.passwordUpdatedAt}
        onChanged={(r) => {
          setLastChange(
            `Password changed. ${sentence(r.otherSessionsEnded, r.trustedDevicesEnded)}`,
          );
          void load();
        }}
        onFailure={failed}
      />

      <AuthenticatorPanel
        confirmedAt={view.twoFactorConfirmedAt}
        pendingExpiresAt={view.pendingTwoFactorExpiresAt}
        onStarted={() => void load()}
        onReplaced={() => {
          setLastChange(
            'Authenticator replaced. Your recovery codes were replaced with it — the new set is ' +
              'shown once, above.',
          );
          void load();
        }}
        onFailure={failed}
      />

      <RecoveryCodesPanel
        remaining={view.recoveryCodesRemaining}
        canMint={view.twoFactorConfirmedAt !== null}
        onMinted={() => void load()}
        onFailure={failed}
      />

      <Panel title="Where you are signed in">
        <div className="stack">
          <div className="stat-row">
            <Stat label="Other sessions" value={String(view.otherSessions)} />
            <Stat label="Saved devices" value={String(view.trustedDevices)} />
          </div>
          <p className="text-subtle">
            A saved device is a browser where you ticked &ldquo;Trust this device&rdquo;. Those
            skip the code from your authenticator, which is why every control on this screen
            un-trusts them.
          </p>
          {/* The control itself lives in the account menu, where it has its own
              confirmation dialog. Two buttons for one destructive action would be
              two places for the wording to drift. */}
          <p className="text-subtle">
            To end them all, use <strong>Sign out everywhere</strong> in the account menu at the
            top right. It signs this browser out too.
          </p>
          {view.otherSessions === 0 && view.trustedDevices === 0 && (
            <p className="text-subtle">Nothing else is signed in and no device is saved.</p>
          )}
        </div>
      </Panel>
    </div>
  );
}
